'use client'

import * as React from 'react'
import Image from 'next/image'
import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { cn } from '@/lib/utils'
import { getAvatarUrl, type AvatarSource } from '@/lib/avatar/read'

function Avatar({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        // isolate so AvatarImage's z-10 cannot paint over sibling status/admin pips
        'relative isolate flex size-8 shrink-0 overflow-hidden rounded-full',
        className
      )}
      {...props}
    />
  )
}

type AvatarImageProps = Omit<
  React.ComponentProps<typeof Image>,
  'src' | 'alt' | 'width' | 'height' | 'fill'
> & {
  src?: string | null
  alt?: string
  /** Fires when the image fails so parents can show initials only after error. */
  onImageError?: () => void
  /**
   * Pixel hint for the Next.js Image optimizer. Default 96 covers up to
   * `size-12` avatars at DPR=2. Override for larger placements (profile
   * headers at `size-20`/`size-24` should pass 192+).
   */
  size?: number
  /**
   * Optional variant source. When passed, the component picks the closest
   * pre-baked WebP variant (64/96/256) by `displayCssPx` and serves it
   * with `unoptimized` — bypassing Next.js's optimizer entirely, since the
   * variant is already pre-sized at upload time. Falls back to `src` when
   * the variant lookup misses (corrupt jsonb, missing key, unbackfilled row).
   */
  source?: AvatarSource | null
  /** CSS pixel size of the rendered slot. Picks variant: ≤24→64, ≤48→96, else 256. */
  displayCssPx?: number
}

// Next.js Image optimizer rejects SVGs unless `dangerouslyAllowSVG` is
// enabled (SVGs can embed scripts / external refs). A lot of our org logos
// and seeded avatars are SVG (DiceBear `api.dicebear.com/.../svg`, direct
// `.svg` uploads, etc.), so we bypass the optimizer for those URLs and let
// the browser render the SVG natively via a plain `<img>` — `unoptimized`
// on `next/image` does exactly that. Raster formats (jpg/png/webp/avif from
// Supabase, LinkedIn CDN, user uploads) keep full optimization.
function isLikelySvgUrl(src: string): boolean {
  return /\.svg($|\?)|\/svg($|\?)|[?&]format=svg\b/i.test(src)
}

function isLinkedinCdnUrl(src: string): boolean {
  try {
    const host = new URL(src).host
    return host === 'licdn.com' || host.endsWith('.licdn.com')
  } catch {
    return false
  }
}

function isSupabaseStorageUrl(src: string): boolean {
  return /supabase\.co\/storage\//i.test(src)
}

// We bypass Radix's `AvatarPrimitive.Image` on purpose. Radix preloads the
// raw `src` via a detached `<img>` before rendering, which would double-fetch
// when paired with `next/image` (the detached preload hits the raw Supabase
// URL, `next/image` hits the `/_next/image` optimizer URL — different URLs,
// no shared cache). Instead we render `next/image` absolutely positioned over
// the `AvatarFallback` sibling:
//   - while the image is loading, the browser renders transparent pixels for
//     the <img> box, so the fallback (initials) behind it shows through
//   - once the image decodes, it paints over the fallback
//   - on error, `onError` fires and we unmount, leaving the fallback visible
//
// This is the same pattern LinkedIn / Facebook use on their avatars. We do
// NOT gate on an opacity transition driven by `onLoad` because Suspense
// fallbacks (our chromed skeleton) may be swapped out before hydration
// attaches the load listener, leaving the image stuck invisible. Native
// `<img>` rendering already handles the visual correctly as long as the URL
// doesn't 400 — which is why we detect SVG and pass `unoptimized`.
function AvatarImage({
  className,
  src,
  alt,
  size = 96,
  source,
  displayCssPx,
  onImageError,
  ...props
}: AvatarImageProps) {
  // Variant resolution: source takes precedence; falls back to src on miss.
  const variantUrl = source ? getAvatarUrl(source, displayCssPx ?? size) : null
  const resolvedSrc = variantUrl ?? src ?? null
  // Pre-baked variants are already the right size at the right format —
  // skip Next.js optimization to save a Vercel transformation quota count.
  const usingVariant = variantUrl !== null

  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    setFailed(false)
  }, [resolvedSrc])

  if (!resolvedSrc || failed) return null

  const unoptimized =
    usingVariant ||
    isLikelySvgUrl(resolvedSrc) ||
    isLinkedinCdnUrl(resolvedSrc) ||
    isSupabaseStorageUrl(resolvedSrc)

  return (
    <Image
      data-slot="avatar-image"
      src={resolvedSrc}
      alt={alt ?? ''}
      width={size}
      height={size}
      loading="lazy"
      unoptimized={unoptimized}
      className={cn('absolute inset-0 z-10 aspect-square size-full object-cover', className)}
      onError={() => {
        setFailed(true)
        onImageError?.()
      }}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn('bg-muted flex size-full items-center justify-center rounded-full', className)}
      {...props}
    />
  )
}

export { Avatar, AvatarImage, AvatarFallback }
